import { action, makeObservable, observable } from "mobx";
import BaseService from "./base/BaseService";
import {
	fetchGeeAddressesByPostcode,
	formatUkPostcodeDisplay,
	isValidPostcode,
	mapGeeFlatAddressToWizardRow,
} from "./postcode/geePostcodeLookup";

/**
 * MobX service for postcode lookup (GEE / Melissa via Lockton proxy — same source as activity-insurance-react).
 */
class PostcodeLookupService extends BaseService {
	/** @type {{ postcode?: string, addresses?: Array<unknown> }} */
	@observable
	postcodeData = {};

	@observable
	postcodeLookupLoading = false;

	@observable
	postcodeLookupError = "";

	/** @type {AbortController | null} */
	_lookupAbortController = null;

	constructor(props) {
		super(props);
		makeObservable(this);
	}

	@action
	setPostcodeData = (postcodeData) => {
		this.postcodeData = postcodeData;
	};

	@action
	setPostcodeLookupLoading = (v) => {
		this.postcodeLookupLoading = v;
	};

	@action
	setPostcodeLookupError = (msg) => {
		this.postcodeLookupError = msg || "";
	};

	loadPostcodeData = async (rawPostcode) => {
		const q = String(rawPostcode || "").trim();

		if (!q) {
			this.setPostcodeData({});
			this.setPostcodeLookupError("");
			this.setPostcodeLookupLoading(false);
			return;
		}

		if (!isValidPostcode(q)) {
			return;
		}

		if (this._lookupAbortController) {
			this._lookupAbortController.abort();
		}
		this._lookupAbortController = new AbortController();
		const { signal } = this._lookupAbortController;

		this.setPostcodeLookupLoading(true);
		this.setPostcodeLookupError("");

		try {
			const rows = await fetchGeeAddressesByPostcode(q, { signal });

			if (signal.aborted) return;

			const addresses = (rows || []).map((row) => mapGeeFlatAddressToWizardRow(row));
			const canonical =
				rows.length > 0 && rows[0].postcode
					? formatUkPostcodeDisplay(rows[0].postcode)
					: formatUkPostcodeDisplay(q);

			this.setPostcodeData({
				postcode: canonical,
				addresses,
			});
		} catch (err) {
			if (err?.name === "AbortError") return;
			this.setPostcodeData({});
			this.setPostcodeLookupError(
				"Unable to look up this postcode. You can enter the address manually."
			);
		} finally {
			if (!signal.aborted) {
				this.setPostcodeLookupLoading(false);
			}
		}
	};
}

export default PostcodeLookupService;
